import breakpoints from "assets/theme/base/breakpoints";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ordersService from "services/orders.service";
import dispatchService from "services/dispatch.service";

import AppBar from "@mui/material/AppBar";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import DataTable from "e4acomponents/Tables/DataTable";
import ViewDispatchesProducts from "cruds/fulfillment-center/components/view-dispatches-products";
import ViewOrderProducts from "cruds/fulfillment-center/components/view-order-products";

export default function RecentChanges({
    organization = null
}) {

    const [avatars, setAvatars] = useState([]);

    const [isVisible, setIsVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [tabsOrientation, setTabsOrientation] = useState("horizontal");

    const [orders, setOrders] = useState([]);
    const [ordersTableData, setOrdersTableData] = useState([]);
    const [dispatches, setDispatches] = useState([]);
    const [dispatchesTableData, setDispatchesTableData] = useState([]);

    const [openOrderProductsDialog, setOpenOrderProductsDialog] = useState(false);
    const [openDispatchProductsDialog, setOpenDispatchProductsDialog] = useState(false);
    const [dialogOrder, setDialogOrder] = useState([]);
    const [dialogDispatch, setDialogDispatch] = useState(null);

    const navigate = useNavigate();

    // Fetch recent orders and dispatches on component mount
    useEffect(() => {
        try {
            setIsLoading(true);
            (async () => {
                let orgId = organization ? organization.id : undefined;
                const [ordersResponse, dispatchesResponse] = await Promise.all([
                    ordersService.getRecentOrders(orgId),
                    dispatchService.getRecentDispatches(orgId)
                ]);
                setOrders(ordersResponse.data.attributes);
                setDispatches(dispatchesResponse.data);
                setIsLoading(false);
            })();
        } catch (error) {
            console.error("Error fetching recent changes: ", error);
        }
    }, [organization])

    // Update orders table data when orders change
    useEffect(() => {
        setOrdersTableData(getOrdersRows(orders));
    }, [orders]);

    // Update dispatches table data when dispatches change
    useEffect(() => {
        setDispatchesTableData(getDispatchRows(dispatches));
    }, [dispatches]);

    // Set tabs orientation based on window size
    useEffect(() => {
        // A function that sets the orientation state of the tabs.
        function handleTabsOrientation() {
            return window.innerWidth < breakpoints.values.sm
                ? setTabsOrientation("vertical")
                : setTabsOrientation("horizontal");
        }

        /** 
         The event listener that's calling the handleTabsOrientation function when resizing the window.
        */
        window.addEventListener("resize", handleTabsOrientation);

        // Call the handleTabsOrientation function to set the state with the initial value.
        handleTabsOrientation();

        // Remove event listener on cleanup
        return () => window.removeEventListener("resize", handleTabsOrientation);
    }, [tabsOrientation]);

    // Function to get order rows for the DataTable
    const getOrdersRows = (info) => {
        return info && info.length > 0 ? info.map((item) => {
            return {
                id: item._id,
                orderNum: item.order_num,
                quantity: item.qty,
                dateOfAssignment: formatDate(item.org_changed_at),
                products: item.products,
            };
        }) : [];
    };

    // Function to get dispatch rows for the DataTable
    const getDispatchRows = (info) => {
        return info && info.length > 0 ? info.map((item) => {
            return {
                id: item._id,
                dispatch_num: item.dispatch_num,
                quantity: item.products.length,
                dateOfAssignment: formatDate(item.org_changed_at),
                productIds: item.products,
                status: item.status,
                productIds: item.products,
            };
        }) : [];
    };

    // Function to format date into "time ago" format
    const formatDate = (date) => {
        const now = new Date();

        const changeDate = new Date(date);
        const diffMs = now - changeDate;
        const diffMins = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffWeeks = Math.floor(diffDays / 7);
        const diffMonths = Math.floor(diffDays / 30);

        if (diffMs < 60000) return `${Math.floor(diffMs / 1000)} seconds ago`;
        if (diffMins < 60) return `${diffMins} minutes ago`;
        if (diffHours < 24) return `${diffHours} hours ago`;
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffMonths < 1) return `${diffWeeks} weeks ago`;
        return `${diffMonths} months ago`;
    };

    // Handles the tab value
    const handleSetTabValue = (event, newValue) => {
        setTabValue(newValue);
    };

    // DataTable animation styles
    const dataTableTransition = {
        transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: avatars ? 1 : 0,
        visibility: avatars ? 'visible' : 'hidden',
        maxHeight: avatars ? '2000px' : '0px',
        marginTop: avatars ? '16px' : '0px',
        overflow: 'hidden',
        '& .MuiTableRow-root': {
            opacity: 0,
            animation: 'fadeIn 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards',
            animationDelay: 'calc(var(--row-index) * 50ms)'
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: 0,
                transform: 'translateY(10px)'
            },
            '100%': {
                opacity: 1,
                transform: 'translateY(0)'
            }
        }
    };

    // Columns for the orders DataTable
    const ordersColumns = {
        columns: [
            { Header: "Order #", accessor: "orderNum", width: "15%" },
            { Header: "Assignment Date", accessor: "dateOfAssignment", width: "23%" },
            { Header: "Quantity", accessor: "quantity", width: "12%" },
            {
                Header: "Action",
                accessor: "action",
                width: "35%",
                Cell: ({ row }) => (
                    <MDButton
                        variant="outlined"
                        color="info"
                        size="small"
                        onClick={() => handleOpenOrderProductsDialog(row.original)}
                    >
                        View Products
                    </MDButton>
                ),
            },
        ],

    };

    // Columns for the dispatches DataTable 
    const dispatchColumns = {
        columns: [
            { Header: "Dispatch #", accessor: "dispatch_num", width: "15%" },
            { Header: "Assignment Date", accessor: "dateOfAssignment", width: "23%" },
            { Header: "Quantity", accessor: "quantity", width: "12%" },
            {
                Header: "Action",
                accessor: "action",
                width: "35%",
                Cell: ({ row }) => (
                    <MDButton
                        variant="outlined"
                        color="info"
                        size="small"
                        onClick={() => handleOpenDispatchProductsDialog(row.original)}
                    >
                        View Products
                    </MDButton>
                ),
            },
        ],
    }

    // Open Order Products Dialog
    const handleOpenOrderProductsDialog = (order) => {
        setDialogOrder(order);
        setOpenOrderProductsDialog(true);
    }

    // Close Order Products Dialog
    const handleCloseOrderProductsDialog = () => {
        setDialogOrder({});
        setOpenOrderProductsDialog(false);
    }

    // Open Dispatch's Products Dialog
    const handleOpenDispatchProductsDialog = (dispatch) => {
        setDialogDispatch(dispatch);
        setOpenDispatchProductsDialog(true);
    }

    // Close Dispatch's Products Dialog
    const handleCloseDispatchProductsDialog = () => {
        setDialogDispatch({});
        setOpenDispatchProductsDialog(false);
    }

    return (
        <Card sx={{ width: "100%", overflow: "visible", height: "100%" }}>

            {/* Dialog to view the products of an order*/}
            <ViewOrderProducts
                open={openOrderProductsDialog}
                handleClose={handleCloseOrderProductsDialog}
                products={dialogOrder.products}
                orderNum={dialogOrder.orderNum}
            />
            {/* Dialog to view the products of a dispatch */}
            <ViewDispatchesProducts
                open={openDispatchProductsDialog}
                handleClose={handleCloseDispatchProductsDialog}
                dispatch={dialogDispatch}
            />

            {/* Header: Icon and title */}
            <MDBox display="flex">
                <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width="4rem"
                    height="4rem"
                    variant="gradient"
                    bgColor="warning"
                    color="white"
                    shadow="md"
                    borderRadius="xl"
                    ml={3}
                    mt={-2}
                >
                    <Icon fontSize="medium" color="inherit">
                        published_with_changes
                    </Icon>
                </MDBox>
                <MDTypography variant="h5" sx={{ mt: 2, mb: 1, ml: 2 }}>
                    Recently Assigned
                </MDTypography>

            </MDBox>

            <MDBox p={2}
                sx={{ overflow: "hidden", maxWidth: "100%" }}
            >
                {/* Tabs navigation bar */}
                <AppBar position="static">
                    <Tabs orientation={tabsOrientation} value={tabValue} onChange={handleSetTabValue}>
                        <Tab
                            label="Recent Orders"
                            icon={
                                <Icon fontSize="small" sx={{ mt: -0.25 }}>
                                    receipt_long
                                </Icon>
                            }
                        />
                        <Tab
                            label="Recent Dispatches"
                            icon={
                                <Icon fontSize="small" sx={{ mt: -0.25 }}>
                                    local_shipping
                                </Icon>
                            }
                        />
                    </Tabs>
                </AppBar>

                {isLoading ? (
                    <MDBox
                        p={5}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        width="100%"
                        height="100%"
                    >
                        <CircularProgress />
                    </MDBox>
                ) : (
                    <MDBox>

                        {/* Recent Orders Table */}
                        {(tabValue === 0) && (
                            <MDBox
                                sx={dataTableTransition}
                            >
                                <MDBox>
                                    <MDButton
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        fullWidth
                                        onClick={() => navigate('/fulfillment-center/view-orders')}
                                    >
                                        View All Orders
                                    </MDButton>
                                    <MDBox mt={2}>
                                        <DataTable
                                            table={{ ...ordersColumns, rows: ordersTableData }}
                                            isSorted={false}
                                            entriesPerPage={{ show: false, entries: [5], defaultValue: 5 }}
                                            showTotalEntries={false}
                                            showEntriesPerPage={false}
                                            noEndBorder
                                            noToolbar
                                            tableId="recent-orders-table"
                                        />
                                    </MDBox>
                                </MDBox>
                            </MDBox>
                        )}
                        {/* Recent Dispatches Table */}
                        {(tabValue === 1) && (
                            <MDBox
                                sx={dataTableTransition}
                            >
                                <MDBox>
                                    <MDButton
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        fullWidth
                                        onClick={() => navigate('/fulfillment-center/view-dispatches')}
                                    >
                                        View All Dispatches
                                    </MDButton>
                                    <MDBox mt={2}>
                                        <DataTable
                                            table={{ ...dispatchColumns, rows: dispatchesTableData }}
                                            isSorted={false}
                                            entriesPerPage={{ show: false, entries: [5], defaultValue: 5 }}
                                            showTotalEntries={false}
                                            showEntriesPerPage={false}
                                            noEndBorder
                                            noToolbar
                                            tableId="recent-dispatches-table"
                                        />
                                    </MDBox>
                                </MDBox>
                            </MDBox>
                        )}
                    </MDBox>)}

            </MDBox>

        </Card>
    )
}